import { useEffect, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { rpcCall } from "../ws/client";
import { Order, OrderSide, ListOrdersParams } from "../ws/types";
import { toast } from "./ui/Toast";
import { useQAProfile, isLocalhost } from "../hooks/useQAProfile";
import { useTelegram } from "../providers/TelegramProvider";
import CreateOrder from "./CreateOrder";
import AmountSelectionModal from "./modals/AmountSelectionModal";
import PaymentDetailsModal from "./modals/PaymentDetailsModal";
import OrderCard from "./order/OrderCard";
import QAControls from "./order/QAControls";
import OrderFilters from "./order/OrderFilters";
import "../styles/OrderList.scss";
import { loadBalanceFromCache, isRateLimitError } from "../utils/balanceCache";
import { useNetwork } from "../hooks/useNetwork";

interface Props {
    onNotification?: (data: any) => void;
}

export default function OrderList({ onNotification }: Props) {
    const wallet = useTonWallet();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user: tgUser } = useTelegram();
    const { network } = useNetwork();
    const { isQAMode, profile, buyerWallet, toggleQA, switchProfile, currentWallet: qaWallet } = useQAProfile(wallet?.account?.address);
    
    const effectiveWallet = qaWallet || wallet?.account?.address || "";
    const [orders, setOrders] = useState<Order[]>([]);
    const [ratings, setRatings] = useState<Record<string, any>>({});
    // Добавляем тип "my" для фильтрации только своих ордеров
    type FilterType = OrderSide | "all" | "my";
    const [filter, setFilter] = useState<FilterType>("all");
    const [jettonFilter, setJettonFilter] = useState<string>("all");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>("all");
    const [loading, setLoading] = useState(false);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [showCreateOrder, setShowCreateOrder] = useState(false);
    
    // Modals state
    const [amountModalData, setAmountModalData] = useState<{
        order: Order;
        message?: string;
    } | null>(null);
    
    const [paymentDetailsModalData, setPaymentDetailsModalData] = useState<{
        orderId: string;
        message?: string;
        amount?: number;
    } | null>(null);

    // Apply filters from URL parameters on mount
    useEffect(() => {
        const sideParam = searchParams.get('side');
        const jettonParam = searchParams.get('jetton');
        
        if (sideParam && ['buy', 'sell', 'all'].includes(sideParam)) {
            setFilter(sideParam as OrderSide | "all");
        }
        
        if (jettonParam) {
            setJettonFilter(jettonParam);
        }
        
        // Clean up URL parameters after applying
        if (sideParam || jettonParam) {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('side');
            newParams.delete('jetton');
            setSearchParams(newParams, { replace: true });
        }
    }, [searchParams]);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const params: ListOrdersParams = {
                status: "active"
            };
            if (filter === "buy" || filter === "sell") {
                params.side = filter;
            }

            const response = await rpcCall<ListOrdersParams, { orders: Order[] }>(
                "orders.list",
                params
            );

            if (response.result) {
                let filteredOrders = response.result.orders;
                // Фильтр "Мои"
                if (filter === "my" && effectiveWallet) {
                    filteredOrders = filteredOrders.filter(order => order.creator === effectiveWallet);
                }
                // Фильтр по жетонам
                if (jettonFilter !== "all") {
                    if (jettonFilter === "other") {
                        // "Другие" - все кроме TMC, USDT, ODDB
                        filteredOrders = filteredOrders.filter(order => 
                            !["TMC", "USDT", "ODDB"].includes(order.jetton_symbol)
                        );
                    } else {
                        filteredOrders = filteredOrders.filter(order => 
                            order.jetton_symbol === jettonFilter
                        );
                    }
                }
                // Фильтр по платежным методам
                if (paymentMethodFilter !== "all") {
                    filteredOrders = filteredOrders.filter(order => {
                        const hasMethod = order.payment_methods && order.payment_methods.includes(paymentMethodFilter);
                        return hasMethod;
                    });
                }
                setOrders(filteredOrders);
                
                // Load ratings for order creators
                const wallets = filteredOrders.map(o => o.creator).filter(Boolean);
                if (wallets.length > 0) {
                    const ratingsResponse = await rpcCall<{ wallets: string[] }, { ratings: Record<string, any> }>(
                        "user.ratings",
                        { wallets }
                    );
                    if (ratingsResponse.result?.ratings) {
                        setRatings(ratingsResponse.result.ratings);
                    }
                }
            }
        } catch (err) {
            console.error("Failed to load orders:", err);
            toast.error("Не удалось загрузить ордера");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadOrders();
    }, [filter, jettonFilter, paymentMethodFilter]);

    const loadPaymentDetails = async () => {
        // This is now handled inside PaymentDetailsModal component
    };

    const handleTakeOrder = async (orderId: string) => {
        if (!effectiveWallet) {
            toast.warning("Подключите кошелёк");
            return;
        }

        const order = orders.find(o => o.id === orderId);
        if (!order) {
            toast.error("Ордер не найден");
            return;
        }

        const message = prompt("Добавьте сообщение для создателя ордера (необязательно):");
        setAmountModalData({ order, message: message || undefined });
    };

    const handleAmountSubmit = async (amount: number) => {
        if (!amountModalData || !effectiveWallet) return;

        const order = amountModalData.order;
        const message = amountModalData.message;

        // Если ордер на покупку крипты - проверяем баланс продавца
        if (order.side === "buy" && !isLocalhost()) {
            try {
                toast.info("Проверка баланса...");
                const balanceResponse = await rpcCall<
                    { wallet: string; jetton_address: string; amount: number },
                    { can_sell: boolean; jetton_balance: number; ton_balance: number; min_ton_required?: number; errors: { insufficient_ton: boolean; insufficient_jettons: boolean } }
                >("wallet.check_balance", {
                    wallet: effectiveWallet,
                    jetton_address: order.jetton_address,
                    amount: amount
                });

                if (balanceResponse.error) {
                    if (isRateLimitError(balanceResponse.error.message)) {
                        const cached = loadBalanceFromCache(effectiveWallet, order.jetton_address, amount, network);
                        if (cached && !cached.can_sell) {
                            toast.error(`Недостаточно ${order.jetton_symbol}`);
                            setAmountModalData(null);
                            return;
                        }
                    } else {
                        toast.error("Ошибка проверки баланса: " + balanceResponse.error.message);
                        setAmountModalData(null);
                        return;
                    }
                } else if (balanceResponse.result && !balanceResponse.result.can_sell) {
                    toast.error(`Недостаточно ${order.jetton_symbol}`);
                    setAmountModalData(null);
                    return;
                }
            } catch (err) {
                console.error("Balance check failed:", err);
                toast.error("Не удалось проверить баланс");
                setAmountModalData(null);
                return;
            }
        }

        // Для BUY ордеров открываем выбор реквизитов
        if (order.side === "buy") {
            setPaymentDetailsModalData({ orderId: order.id, message, amount });
            setAmountModalData(null);
            return;
        }

        // Для SELL ордеров отправляем заявку напрямую
        await submitDealRequest(order.id, message, amount);
        setAmountModalData(null);
        navigate("/?tab=requests");
    };

    const submitDealRequest = async (orderId: string, message?: string, amount?: number, paymentDetailId?: string) => {
        try {
            const response = await rpcCall(
                "orders.take",
                {
                    order_id: orderId,
                    taker_wallet: effectiveWallet,
                    message: message,
                    amount: amount,
                    payment_detail_id: paymentDetailId,
                    force: isLocalhost(),
                    tg_user_id: tgUser?.id
                }
            );

            if (response.error) {
                toast.error(response.error.message);
            } else {
                toast.success("Заявка отправлена! Ожидайте подтверждения.");
                setPaymentDetailsModalData(null);
                loadOrders();
                navigate("/?tab=requests");
            }
        } catch (err) {
            console.error(err);
            toast.error("Ошибка при отправке заявки");
        }
    };

    const handlePaymentDetailsSubmit = async (paymentDetailId?: string) => {
        if (!paymentDetailsModalData) return;

        await submitDealRequest(
            paymentDetailsModalData.orderId,
            paymentDetailsModalData.message,
            paymentDetailsModalData.amount,
            paymentDetailId
        );
    };

    const handleCancelOrder = async (orderId: string) => {
        if (!effectiveWallet) {
            toast.warning("Подключите кошелёк");
            return;
        }

        if (!confirm("Вы уверены, что хотите отменить этот ордер?")) {
            return;
        }

        console.log('[OrderList] Cancelling order:', orderId, 'wallet:', effectiveWallet);
        setCancellingId(orderId);
        try {
            const response = await rpcCall(
                "orders.cancel",
                {
                    order_id: orderId,
                    wallet: effectiveWallet
                }
            );

            console.log('[OrderList] Cancel response:', response);

            if (response.error) {
                console.error('[OrderList] Cancel error:', response.error);
                toast.error(response.error.message);
            } else {
                console.log('[OrderList] Order cancelled successfully');
                toast.success("Ордер отменён");
                loadOrders();
            }
        } catch (err) {
            console.error('[OrderList] Cancel exception:', err);
            toast.error("Ошибка при отмене ордера");
        } finally {
            setCancellingId(null);
        }
    };

    const isMyOrder = (creator: string): boolean => {
        if (isQAMode && profile === 'buyer') {
            return false;
        }
        return Boolean(effectiveWallet && creator === effectiveWallet);
    };

    return (
        <div className="order-list">
            {/* Modals */}
            {amountModalData && (
                <AmountSelectionModal
                    order={amountModalData.order}
                    message={amountModalData.message}
                    isOpen={true}
                    onClose={() => setAmountModalData(null)}
                    onSubmit={handleAmountSubmit}
                />
            )}

            {paymentDetailsModalData && (
                <PaymentDetailsModal
                    orderId={paymentDetailsModalData.orderId}
                    message={paymentDetailsModalData.message}
                    amount={paymentDetailsModalData.amount}
                    wallet={effectiveWallet}
                    isOpen={true}
                    onClose={() => setPaymentDetailsModalData(null)}
                    onSubmit={handlePaymentDetailsSubmit}
                />
            )}

            {/* QA Controls */}
            <QAControls
                isQAMode={isQAMode}
                profile={profile}
                onToggle={toggleQA}
                onSwitchProfile={switchProfile}
            />

            {/* Header */}
            <div className="list-header">
                <div className="header-actions">
                    <h2 style={{ color: "#dcdcdc", fontSize: "1.2rem" }}>{showCreateOrder ? 'Создать ордер' : 'Активные ордера'}</h2>
                    <button 
                        className="btn-create-order"
                        onClick={() => setShowCreateOrder(!showCreateOrder)}
                    >
                        {showCreateOrder ? '❌ Закрыть' : '➕ Создать ордер'}
                    </button>
                </div>
            </div>

            {/* Create Order Section */}
            {showCreateOrder ? (
                <div className="create-order-section">
                    <CreateOrder onSuccess={() => {
                        setShowCreateOrder(false);
                        loadOrders();
                    }} />
                </div>
            ) : (
                <>
                    {/* Filters */}
                    <OrderFilters
                        filter={filter}
                        jettonFilter={jettonFilter}
                        paymentMethodFilter={paymentMethodFilter}
                        onFilterChange={setFilter}
                        onJettonFilterChange={setJettonFilter}
                        onPaymentMethodFilterChange={setPaymentMethodFilter}
                    />

                    {/* QA Toggle for non-QA mode */}
                    {!isQAMode && isLocalhost() && (
                        <div className="qa-header-toggle">
                            <label className="qa-toggle-inline">
                                <input 
                                    type="checkbox" 
                                    checked={isQAMode}
                                    onChange={toggleQA}
                                />
                                🔧 QA Mode (для тестирования)
                            </label>
                        </div>
                    )}

                    {/* Orders List */}
                    {loading ? (
                <div className="loading-container">
                    <div className="spinner" />
                    <span className="loading-text">Загрузка ордеров...</span>
                </div>
            ) : orders.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <div className="empty-state-title">Нет активных ордеров</div>
                    <div className="empty-state-description">
                        Создайте новый ордер или подождите, пока появятся новые
                    </div>
                </div>
            ) : (
                <div className="orders-grid">
                    {orders.map((order) => (
                        <OrderCard
                            key={order.id}
                            order={order}
                            rating={ratings[order.creator]}
                            isMyOrder={isMyOrder(order.creator)}
                            isQAMode={isQAMode}
                            qaProfile={profile}
                            onTake={handleTakeOrder}
                            onCancel={handleCancelOrder}
                            isCancelling={cancellingId === order.id}
                        />
                    ))}
                </div>
            )}
                </>
            )}
        </div>
    );
}

